import React, { useEffect, useRef, useState } from 'react';
import { Input, Select } from 'antd';
import { getCountries, getCountryCallingCode, parsePhoneNumber, type CountryCode } from 'libphonenumber-js';
import { parsePhone, toFlagEmoji } from '@/utils/parsePhone';

const PRIORITY_COUNTRIES: CountryCode[] = ['DE', 'AT', 'CH', 'US', 'GB', 'FR', 'NL', 'BE', 'IT', 'ES', 'PL'];

const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });

const makeOption = (cc: CountryCode) => ({
  value: cc,
  label: `${toFlagEmoji(cc)} +${getCountryCallingCode(cc)}`,
  search: `${cc} +${getCountryCallingCode(cc)} ${displayNames.of(cc) ?? ''}`.toLowerCase(),
});

const UNKNOWN_OPTION = { value: '__unknown__', label: '—', disabled: true, search: '' };

const COUNTRY_OPTIONS = [
  UNKNOWN_OPTION,
  ...PRIORITY_COUNTRIES.map(makeOption),
  { value: '__sep__', label: '──────────────', disabled: true, search: '' },
  ...getCountries()
    .filter((cc) => !PRIORITY_COUNTRIES.includes(cc))
    .sort()
    .map(makeOption),
];

type CountryState = CountryCode | null; // null = unknown/unparseable

type Props = {
  value?: string;
  onChange?: (e164: string) => void;
};

export default function PhoneNumberInput({ value, onChange }: Props) {
  const [country, setCountry] = useState<CountryState>(null);
  const [national, setNational] = useState('');

  const wrapperRef = useRef<HTMLDivElement>(null);
  const countryRef = useRef<CountryState>(null);
  const nationalRef = useRef('');
  const onChangeRef = useRef(onChange);
  // True while the country Select dropdown is open — prevents the wrapper's
  // onBlur from firing during keyboard-driven dropdown navigation, because
  // Ant Design renders the dropdown in a portal (outside the wrapper DOM).
  const selectOpenRef = useRef(false);

  countryRef.current = country;
  nationalRef.current = national;
  onChangeRef.current = onChange;

  useEffect(() => {
    const parsed = parsePhone(value);
    if (parsed) {
      setCountry(parsed.country as CountryCode);
      setNational(parsed.national);
    } else {
      setCountry(null);
      setNational(value || '');
    }
  }, [value]);

  const tryEmit = (nationalValue: string, countryCode: CountryState) => {
    if (!nationalValue) return;
    try {
      const p = countryCode
        ? parsePhoneNumber(nationalValue, countryCode)
        : parsePhoneNumber(nationalValue); // works if user typed +XX... without selecting a flag
      if (p?.isValid()) onChangeRef.current?.(p.number as string);
    } catch {
      // not a valid/parseable number yet
    }
  };

  return (
    <div
      ref={wrapperRef}
      onBlur={(e) => {
        if (selectOpenRef.current) return;
        if (!wrapperRef.current?.contains(e.relatedTarget as Node)) {
          tryEmit(nationalRef.current, countryRef.current);
        }
      }}
    >
      <Input
        addonBefore={
          <Select
            value={country ?? '__unknown__'}
            onChange={(cc: string) => {
              const code = cc as CountryCode;
              countryRef.current = code;
              setCountry(code);
              tryEmit(nationalRef.current, code);
            }}
            onDropdownVisibleChange={(open) => {
              selectOpenRef.current = open;
            }}
            showSearch
            filterOption={(input, option: any) =>
              option?.search?.includes(input.toLowerCase()) ?? false
            }
            options={COUNTRY_OPTIONS}
            popupMatchSelectWidth={false}
            style={{ width: 85 }}
            dropdownStyle={{ minWidth: 200 }}
            labelRender={({ value: v }) =>
              v === '__unknown__' ? '—' : toFlagEmoji(v as string)
            }
          />
        }
        value={national}
        onChange={(e) => {
          nationalRef.current = e.target.value;
          setNational(e.target.value);
        }}
      />
    </div>
  );
}
